import { isNewRecordId } from "@/features/catalog_case_review/shared/utils/catalogBlankRecord";
import type { FieldActionContext } from "@/features/catalog_case_review/shared/model/fieldActionContext";
import type { PageLocation } from "../model/pageLocation";

/** One AG-UI `RunAgentInput.context` entry: what the value is, and the value. */
export interface AguiContextEntry {
  description: string;
  value: string;
}

/**
 * The description the server reads the screen label from (`AguiScreenLabel.SCREEN_DESCRIPTION`).
 * `aguiContextEntries.test.ts` pins it against the Java constant.
 */
export const SCREEN_DESCRIPTION = "The screen the user is looking at";

/**
 * The server's cap on the rendered instruction text (`AguiContextInstruction`). The test measures the
 * JSON of the entries, which is longer than the rendered `description: value` lines, so it is conservative.
 */
export const CONTEXT_CHARACTER_CAP = 4000;

/**
 * The description the server reads a field action from (`FieldActionContext` in `case-db-service`).
 * Its value is the JSON of the `FieldActionContext` the button built: identifiers only.
 */
export const FIELD_ACTION_DESCRIPTION = "Field action the user started";

export const ENTRY_DESCRIPTIONS = {
  screen: SCREEN_DESCRIPTION,
  page: "The kind of screen this is",
  caseId: "The id of the case on this screen — read its values through your tools, never from here",
  section: "The catalog section the user has open",
  group: "The group of fields shown inside that section",
  collection: "The collection of records shown inside that section",
  record: "The record selected inside that collection",
  unsavedRecord: "The record selected inside that collection; it is unsaved, so server tools cannot see it",
  editableFields: "The fields you may propose values for right now, by id",
  focusedField: "The field the user has focused",
  mode: "Whether the user is reading or editing the case",
  lock: "Who holds the case's edit lock",
  dirty: "Whether the user has unsaved changes",
  pendingProposals: "Whether proposals from earlier turns are still waiting for a decision",
  panel: "The panel open on this screen",
  filter: (id: string) => `A filter the user has applied, by id: ${id}`,
  textFilter: (id: string) => `Whether the user has typed into the ${id} filter; the text itself is not shared`,
  capabilities: "What this user may do here. Advisory only — say so when an action needs a role they lack, rather than proposing it. This is never authorization: every tool authorizes server-side under their own token",
} as const;

/**
 * What the model is told when the screen published nothing. Not an empty list: silence reads as
 * permission to guess, so the blindness is stated instead.
 */
export const BLIND: AguiContextEntry = {
  description: "Whether you can see the user's screen",
  value:
    "No. This screen published nothing about itself, so you do not know which page they are on," +
    " what is on it, or what they can do there. Say so plainly if they ask, and ask them where" +
    " they are — never name or describe a screen you were not told about.",
};

const LIST_SEPARATOR = ", ";

/** The entry a field action rides on: sent with the button's run and each auto-continue run of that turn. */
export function fieldActionEntry(context: FieldActionContext): AguiContextEntry {
  return { description: FIELD_ACTION_DESCRIPTION, value: JSON.stringify(context) };
}

/** Turns a screen's location into the context entries a run carries: ids, enums, booleans, a route. */
export function entriesFor(location: PageLocation | null): AguiContextEntry[] {
  if (location === null) return [BLIND];
  const entries: AguiContextEntry[] = [
    { description: ENTRY_DESCRIPTIONS.screen, value: `${location.title} (${location.route})` },
    { description: ENTRY_DESCRIPTIONS.page, value: location.page },
  ];
  if (location.caseId !== null) entries.push({ description: ENTRY_DESCRIPTIONS.caseId, value: String(location.caseId) });
  if (location.section !== null) {
    const section = location.section;
    entries.push({ description: ENTRY_DESCRIPTIONS.section, value: section.id });
    if (section.group !== null) entries.push({ description: ENTRY_DESCRIPTIONS.group, value: section.group });
    if (section.collectionId !== null) entries.push({ description: ENTRY_DESCRIPTIONS.collection, value: section.collectionId });
    if (section.recordId !== null) {
      entries.push({
        description: isNewRecordId(section.recordId) ? ENTRY_DESCRIPTIONS.unsavedRecord : ENTRY_DESCRIPTIONS.record,
        value: section.recordId,
      });
    }
    if (section.editableFieldIds.length > 0) {
      entries.push({ description: ENTRY_DESCRIPTIONS.editableFields, value: section.editableFieldIds.join(LIST_SEPARATOR) });
    }
  }
  if (location.focusedFieldId !== null) entries.push({ description: ENTRY_DESCRIPTIONS.focusedField, value: location.focusedFieldId });
  entries.push({ description: ENTRY_DESCRIPTIONS.mode, value: location.mode });
  entries.push({ description: ENTRY_DESCRIPTIONS.lock, value: location.lock });
  entries.push({ description: ENTRY_DESCRIPTIONS.dirty, value: String(location.dirty) });
  entries.push({ description: ENTRY_DESCRIPTIONS.pendingProposals, value: String(location.pendingProposals > 0) });
  if (location.panel !== null) entries.push({ description: ENTRY_DESCRIPTIONS.panel, value: location.panel });
  for (const filter of location.filters) {
    if (filter.value !== null) entries.push({ description: ENTRY_DESCRIPTIONS.filter(filter.id), value: filter.value });
    else entries.push({ description: ENTRY_DESCRIPTIONS.textFilter(filter.id), value: String(filter.hasText) });
  }
  if (location.capabilities.length > 0) {
    entries.push({ description: ENTRY_DESCRIPTIONS.capabilities, value: location.capabilities.join(LIST_SEPARATOR) });
  }
  return entries;
}
